import { useMemo } from "react";
import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { WfPopover } from "src/components/base/wf-popover";
import { ReactionBarSegment } from "src/components/common/aggregated-reactions-bar/reaction-bar-segment";
import { IAggregatedReactionsBar } from "src/components/common/aggregated-reactions-bar/types";
import { getReactionsCount } from "src/components/common/aggregated-reactions-bar/utils/get-reactions-count";
import { mapAggregatedReactionsToOrderedBarSegments } from "src/components/common/aggregated-reactions-bar/utils/map-aggregated-reactions-to-ordered-bar-segments";
import { createAggregatedReactionsPageUrl } from "src/consts/urls";
import { calculatePercentage } from "src/utils/numbers/calculate-percentage";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { AGGREGATED_REACTIONS_BAR_TEST_ID } from "./aggregated-reactions-bar.test-ids";

export const AggregatedReactionsBar = ({ reactions, topNumberOfReactions }: IAggregatedReactionsBar) => {
    const { country, language } = useRouterLocale();
    const { t } = useTranslation("reactions");
    const reactionsSum = useMemo(() => getReactionsCount(reactions, "numberOfReactions"), [reactions]);
    const weightedReactionsSum = useMemo(() => getReactionsCount(reactions, "weightedNumberOfReactions"), [reactions]);
    const mappedReactions = useMemo(() => mapAggregatedReactionsToOrderedBarSegments(reactions), [reactions]);

    const cap = reactionsSum > topNumberOfReactions ? reactionsSum : topNumberOfReactions;
    const reactionsPercentage = calculatePercentage(reactionsSum, cap);

    return (
        <WfPopover
            content={t("aggregated-reactions.tooltip-content")}
            link={{ name: t("aggregated-reactions.tooltip-link-label"), url: createAggregatedReactionsPageUrl(language, country) }}
        >
            <Box
                display="flex"
                h={3}
                borderRadius="16px"
                borderWidth="1px"
                borderColor="gray.200"
                overflow="hidden"
                data-test-id={AGGREGATED_REACTIONS_BAR_TEST_ID}
                fontSize="md"
                cursor="pointer"
            >
                <Box display="flex" w={`${reactionsPercentage}%`}>
                    {mappedReactions.map(
                        reaction =>
                            reaction.value > 0 && (
                                <ReactionBarSegment
                                    width={calculatePercentage(reaction.weightedValue, weightedReactionsSum)}
                                    key={reaction.color}
                                    color={reaction.color}
                                />
                            )
                    )}
                </Box>
            </Box>
        </WfPopover>
    );
};
